import { __awaiter } from 'tslib';
import { IMEvent, ErrorType, InfoKeys } from '../consts.js';
import { BaseError } from '../errors/BaseError.js';
import { Participant } from '../model/Participant.js';
import { im_proto } from '../../proto/index.js';
import { Logger } from '../metrics/logger.js';
import * as Long from 'long';
import { ContextClassBase, ServiceIdentity } from './RuntimeContext.js';

class ParticipantManager extends ContextClassBase {
    constructor(ctx) {
        super(ctx);
        this.participants = new Map();
    }
    applyLocal(ps) {
        ps.forEach(p => {
            if (!this.participants.has(p.conversationId)) {
                this.participants.set(p.conversationId, new Map());
            }
            const convMap = this.participants.get(p.conversationId);
            convMap.set(p.userId.toString(), p);
        });
    }
    forceApplyLocal(ps) {
        var _a;
        const convMap = new Map();
        ps.forEach(p => {
            convMap.set(p.userId.toString(), p);
        });
        this.participants.set((_a = ps[0]) === null || _a === void 0 ? void 0 : _a.conversationId, convMap);
    }
    clearAll() {
        this.participants.clear();
    }
    updateWithUserIdAsync(conversation, userId) {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            const participants = userId.map(item => Long.fromValue(item));
            const resp = yield this.resolve(ServiceIdentity.CoreApi).GetConversationParticipantByUserId({
                conversationId: conversation.id,
                conversationShortId: Long.fromString(conversation.shortId),
                conversationType: conversation.type,
                inboxType: conversation.inboxType,
                participants
            });
            const participantList = (_d = (_c = (_b = (_a = resp === null || resp === void 0 ? void 0 : resp.body) === null || _a === void 0 ? void 0 : _a.mget_conversation_participants_body) === null || _b === void 0 ? void 0 : _b.participants) === null || _c === void 0 ? void 0 : _c.map(item => Participant.fromServerParticipant(this.ctx, item, conversation, resp.log_id))) !== null && _d !== void 0 ? _d : [];
            this.upsertBatch(conversation.id, participantList);
        });
    }
    upsert(conversationId, p, isSilent = false) {
        var _a;
        let cp = this.participants.get(conversationId.toString());
        if (!cp) {
            cp = new Map();
        }
        let toAdded = p;
        toAdded.userId = toAdded.userId.toString();
        if (cp.has(p.userId.toString())) {
            toAdded = Participant.featLocalParticipant(cp.get(p.userId.toString()), toAdded);
        }
        cp.set(p.userId.toString(), toAdded);
        this.participants.set(conversationId, cp);
        if (!isSilent) {
            this.resolve(ServiceIdentity.EventBus).emit(IMEvent.ParticipantUpsert, this, toAdded);
        }
        void ((_a = this.resolve(ServiceIdentity.DbProxy)) === null || _a === void 0 ? void 0 : _a.upsertParticipant(toAdded));
        return Array.from(cp.values());
    }
    upsertBatch(conversationId, ps, isSilent = false) {
        ps.forEach(p => this.upsert(conversationId, p, isSilent));
        return this.get(conversationId);
    }
    addBatch(conversationId, ps) {
        const cp = this.participants.get(conversationId);
        ps.forEach(p => {
            if (!(cp === null || cp === void 0 ? void 0 : cp.has(p.userId.toString()))) {
                this.resolve(ServiceIdentity.EventBus).emit(IMEvent.ParticipantJoin, this, p);
                // 自己建群、被拉进群
                if (p.userId.toString() === this.ctx.option.userId.toString()) {
                    const conv = this.getContext().resolve(ServiceIdentity.ConversationManager).get(conversationId);
                    this.resolve(ServiceIdentity.EventBus).emit(IMEvent.ConversationJoin, this, conv);
                }
            }
            this.upsert(conversationId, p);
        });
        return this.get(conversationId);
    }
    delete(conversationId, userId) {
        const cp = this.participants.get(conversationId);
        if (!cp) {
            return;
        }
        userId.forEach(u => {
            var _a;
            const oldParticipant = cp.get(u.toString());
            if (oldParticipant === undefined) {
                return;
            }
            cp.delete(u.toString());
            this.resolve(ServiceIdentity.EventBus).emit(IMEvent.ParticipantLeave, this, oldParticipant);
            void ((_a = this.resolve(ServiceIdentity.DbProxy)) === null || _a === void 0 ? void 0 : _a.deleteParticipant(oldParticipant));
        });
    }
    get(conversationId) {
        const p = this.participants.get(conversationId);
        if (p) {
            return Array.from(p.values());
        }
        else {
            throw new BaseError({
                ctx: this.ctx,
                type: ErrorType.ConversationNotExist,
                msg: `conversation ${conversationId} participants is not loaded`,
                sender: this,
                args: {
                    conversationId
                }
            });
        }
    }
    getParticipant(conversation) {
        var _a, _b, _c, _d, _e;
        const participants = [];
        const current = this.getRaw(conversation.id);
        if ((current === null || current === void 0 ? void 0 : current.length) > 0) {
            return current;
        }
        else {
            if (Number((_b = (_a = conversation === null || conversation === void 0 ? void 0 : conversation.firstPageParticipant) === null || _a === void 0 ? void 0 : _a.participants) === null || _b === void 0 ? void 0 : _b.length) > 0) {
                (((_c = conversation === null || conversation === void 0 ? void 0 : conversation.firstPageParticipant) === null || _c === void 0 ? void 0 : _c.participants) || []).forEach(p => {
                    participants.push(Participant.fromServerParticipant(this.ctx, p, conversation, conversation.ext[InfoKeys.LocalLogId]));
                });
            }
            else if (conversation.type === im_proto.ConversationType.ONE_TO_ONE_CHAT) {
                Logger.ctxDebug(this.ctx, 'no first page participant found, fallback to local');
                const currentUsers = [conversation.toParticipantUserId, this.ctx.option.userId];
                currentUsers.forEach(u => {
                    if (u) {
                        const fakeP = Participant.fakeParticipant(this.ctx, u, conversation);
                        participants.push(fakeP);
                    }
                });
            }
            this.upsertBatch(conversation.id, participants, true);
            if ((_d = conversation.firstPageParticipant) === null || _d === void 0 ? void 0 : _d.has_more) {
                void this.resolve(ServiceIdentity.CoreApi).GetConversationParticipantsList({
                    conversationId: conversation.id,
                    conversationShortId: Long.fromString(conversation.shortId),
                    conversationType: conversation.type,
                    cursor: (_e = conversation.firstPageParticipant.cursor) !== null && _e !== void 0 ? _e : Long.ZERO,
                    limit: 50,
                    inboxType: conversation.inboxType
                }).then(response => {
                    var _a, _b;
                    const resp = (_b = (_a = response === null || response === void 0 ? void 0 : response.body) === null || _a === void 0 ? void 0 : _a.conversation_participants_body) === null || _b === void 0 ? void 0 : _b.participants_page;
                    resp === null || resp === void 0 ? void 0 : resp.participants.forEach(p => {
                        participants.push(Participant.fromServerParticipant(this.ctx, p, conversation, response === null || response === void 0 ? void 0 : response.log_id));
                    });
                    this.upsertBatch(conversation.id, participants, true);
                });
            }
            return participants;
        }
    }
    getMap(conversationId) {
        const p = this.participants.get(conversationId);
        if (p) {
            return p;
        }
        else {
            throw new BaseError({
                ctx: this.ctx,
                type: ErrorType.ConversationNotExist,
                msg: `conversation ${conversationId} participants is not loaded`,
                sender: this,
                args: {
                    conversationId
                }
            });
        }
    }
    getMapRaw(conversationId) {
        return this.participants.get(conversationId);
    }
    getRaw(conversationId) {
        const p = this.participants.get(conversationId);
        if (p) {
            return Array.from(p.values());
        }
        else {
            return [];
        }
    }
    getByUserIdRaw(conversationId, userId) {
        const ps = this.participants.get(conversationId);
        if (ps) {
            return ps.get(userId.toString());
        }
        return undefined;
    }
    dispose() {
        this.clearAll();
    }
}

export { ParticipantManager };
//# sourceMappingURL=ParticipantManager.js.map
